import { useCallback, useEffect, useMemo, useState } from 'react';
import { allows, allowsAnywhere, ApiError, sites as loadSites, type CurrentUser, type SiteSummary } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import {
  Button,
  EmptyState,
  Field,
  MoneyInput,
  ModulePage,
  Select,
  Skeleton,
  SkeletonRows,
  Table,
  TableSkeleton,
  TextInput,
} from '../../shell/kit';
import { useToast } from '../../shell/toast';
import { useTab } from '../../shell/hooks';
import { formatMoment } from '../../shell/format';
import { PackagesPanel, ServicesPanel } from './catalog-panels';
import {
  closeShift,
  collectCash,
  downloadTimesheet,
  enrollPosDevice,
  fetchAttentionPayments,
  fetchCashiers,
  resolveAttentionPayment,
  type AttentionPayment,
  fetchPosDevices,
  priceHistory,
  revokePosDevice,
  recordMovement,
  recordTimesheet,
  saveMaterial,
  setNorm,
  type CashierOption,
  type MaterialRow,
  fetchCatalog,
  fetchMaterials,
  fetchShift,
  fetchShifts,
  fetchTimesheet,
  openShift,
  reconcileShift,
  setTariff,
  type CatalogEntry,
  type EmployeeResult,
  type PosDevice,
  type PriceHistoryRow,
  type ShiftDetail,
  type ShiftRow,
  type StockRow,
  type TimesheetEntry,
} from './api';
import { makePosT, money, type PosKey, type PosTranslate } from './i18n';

type TabKey = 'shifts' | 'catalog' | 'materials' | 'timesheet' | 'devices' | 'attention';

/**
 * Вкладки гейтятся правами так же, как разделы в левом меню: показывать вкладку,
 * которая гарантированно ответит 403, — то же самое, что показывать кассиру
 * «Обмен и миграцию». Смены доступны тому, кто их открывает; каталог, материалы
 * и табель — тем, у кого есть отчётное право или управление каталогом.
 */
const TABS: ReadonlyArray<{
  readonly key: TabKey;
  readonly label: PosKey;
  readonly needs: readonly string[];
}> = [
  { key: 'shifts', label: 'tab.shifts', needs: ['shift.open', 'shift.close', 'report.site'] },
  { key: 'catalog', label: 'tab.catalog', needs: ['catalog.manage', 'report.site'] },
  { key: 'materials', label: 'tab.materials', needs: ['catalog.manage', 'report.site'] },
  { key: 'timesheet', label: 'tab.timesheet', needs: ['report.site'] },
  { key: 'devices', label: 'tab.devices', needs: ['admin.manage'] },
  { key: 'attention', label: 'tab.attention', needs: ['report.site', 'order.refund'] },
];

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface">
      <h3 className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">{title}</h3>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-1.5 last:border-0">
      <span className="text-sm text-ink-muted">{label}</span>
      <span
        className={`tabular-nums text-sm font-medium ${tone === 'bad' ? 'text-danger' : 'text-ink'}`}
      >
        {value}
      </span>
    </div>
  );
}

// Неизвестный способ (например, историческая запись до отказа от bonus/
// subscription на кассе) показываем как есть, а не сырым ключом словаря.
function methodLabel(t: PosTranslate, method: string): string {
  const key = `method.${method}` as PosKey;
  const label = t(key);
  return label === key ? method : label;
}

function ShiftsTab({ siteId, t, canReconcile }: { siteId: string; t: PosTranslate; canReconcile: boolean }) {
  const [shifts, setShifts] = useState<readonly ShiftRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<ShiftDetail | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const [opening, setOpening] = useState('0');
  const [counted, setCounted] = useState('0');
  const [comment, setComment] = useState('');
  const [collectMinor, setCollectMinor] = useState<number | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setBusy(true);
    try {
      const rows = await fetchShifts(siteId);
      setShifts(rows);
      const current = rows[0];
      setDetail(current ? await fetchShift(current.id) : undefined);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
      setLoading(false);
    }
  }, [siteId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      // Ответ показывается рядом с кнопкой: раньше сырой код ошибки уезжал
      // в плашку вверху страницы («E07.discrepancy_comment_required»).
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
      setBusy(false);
    }
  };

  const active = detail?.shift;
  const position = detail?.position;
  const difference =
    active && active.cashClosingMinor !== null && position
      ? active.cashClosingMinor - position.expectedMinor
      : null;

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t('shift.status')}>
          {loading ? (
            <div className="flex flex-col gap-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-5" />
              ))}
            </div>
          ) : active ? (
            <>
              <Row label={t('shift.status')} value={t(`shift.status.${active.status}` as PosKey)} />
              <Row label={t('shift.openedAt')} value={new Date(active.openedAt).toLocaleString()} />
              <Row label={t('shift.opening')} value={money(active.cashOpeningMinor)} />
              <Row
                label={t('shift.cashPayments')}
                value={money(position?.cashPaymentsMinor ?? 0)}
              />
              <Row label={t('shift.cashRefunds')} value={money(position?.cashRefundsMinor ?? 0)} />
              <Row label={t('shift.collected')} value={money(position?.collectedMinor ?? 0)} />
              <Row label={t('shift.expected')} value={money(position?.expectedMinor ?? 0)} />
              {active.cashClosingMinor !== null ? (
                <Row label={t('shift.closing')} value={money(active.cashClosingMinor)} />
              ) : null}
              {difference !== null ? (
                <Row
                  label={t('shift.difference')}
                  value={money(difference)}
                  {...(difference === 0 ? {} : { tone: 'bad' as const })}
                />
              ) : null}
            </>
          ) : (
            <p className="text-sm text-ink-muted">{t('common.empty')}</p>
          )}

          <div className="mt-4 flex flex-wrap items-end gap-2">
            {!active || active.status !== 'open' ? (
              <>
                <label className="text-sm text-ink-muted">
                  {t('shift.opening')}
                  <TextInput
                    value={opening}
                    onChange={(e) => setOpening(e.target.value)}
                    inputMode="numeric"
                    className="mt-1 w-32"
                  />
                </label>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void act(() => openShift(siteId, Math.round(Number(opening) * 100)))
                  }
                  className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
                >
                  {t('shift.open')}
                </button>
              </>
            ) : null}

            {active?.status === 'open' ? (
              <>
                {/* Инкассация: эндпоинт был, а формы не было ни в одном состоянии
                    смены — поле «Инкассировано» на экране всегда показывало ноль. */}
                <label className="text-sm text-ink-muted">
                  {t('shift.collectAmount')}
                  <div className="mt-1 w-40">
                    <MoneyInput valueMinor={collectMinor} onChangeMinor={setCollectMinor} />
                  </div>
                </label>
                <button
                  type="button"
                  disabled={busy || !collectMinor || collectMinor <= 0}
                  onClick={() =>
                    void act(async () => {
                      await collectCash(active.id, collectMinor ?? 0, comment.trim() || undefined);
                      setCollectMinor(null);
                      setComment('');
                    })
                  }
                  className="rounded border border-line-strong px-3 py-2 text-sm font-medium text-ink hover:bg-sunken disabled:text-ink-faint"
                >
                  {t('shift.collect')}
                </button>
                <label className="text-sm text-ink-muted">
                  {t('shift.closing')}
                  <TextInput
                    value={counted}
                    onChange={(e) => setCounted(e.target.value)}
                    inputMode="numeric"
                    className="mt-1 w-32"
                  />
                </label>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void act(() => closeShift(active.id, Math.round(Number(counted) * 100)))
                  }
                  className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
                >
                  {t('shift.close')}
                </button>
              </>
            ) : null}

            {active?.status === 'closed' && !canReconcile ? (
              <p className="text-sm text-ink-muted">{t('shift.reconcileOwn')}</p>
            ) : null}
            {active?.status === 'closed' && canReconcile ? (
              <>
                <label className="grow text-sm text-ink-muted">
                  {t('common.comment')}
                  <TextInput
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('shift.discrepancy')}
                    className="mt-1"
                  />
                </label>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void act(() => reconcileShift(active.id, comment.trim() || undefined))
                  }
                  className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
                >
                  {t('shift.reconcile')}
                </button>
              </>
            ) : null}
          </div>
        </Panel>

        <Panel title={t('shift.movements')}>
          {loading ? (
            <TableSkeleton rows={3} columns={4} />
          ) : (detail?.movements ?? []).length === 0 ? (
            <p className="text-sm text-ink-muted">{t('common.empty')}</p>
          ) : (
            <Table>
              <tbody>
                {(detail?.movements ?? []).map((m) => (
                  <tr key={m.id} className="border-b border-line last:border-0">
                    <td className="py-2 text-ink">{t(`shift.kind.${m.kind}` as PosKey)}</td>
                    <td className="py-2 text-ink-muted">{m.comment ?? ''}</td>
                    <td className="py-2 text-right tabular-nums text-ink">
                      {money(m.amountMinor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Panel>
      </div>

      {/*
        * «Состояние» слева считает только наличные — это ящик, и так и
        * должно быть. Карта, Kaspi и счёт не наличные, и раньше по ним не
        * было видно ничего вообще: ни оплат, ни возвратов, ни здесь, ни где
        * либо ещё. Эта таблица — не про закрытие смены, просто витрина по
        * каждому способу отдельно, с возвратами на виду.
        */}
      <Panel title={t('shift.byMethod')}>
        {loading ? (
          <TableSkeleton rows={3} columns={5} />
        ) : (detail?.byMethod ?? []).length === 0 ? (
          <p className="text-sm text-ink-muted">{t('common.empty')}</p>
        ) : (
          <Table>
            <thead>
              <tr className="text-left text-ink-muted">
                <th className="pb-2 font-medium">{t('shift.method')}</th>
                <th className="pb-2 text-right font-medium">{t('shift.methodPayments')}</th>
                <th className="pb-2 text-right font-medium">{t('shift.methodCaptured')}</th>
                <th className="pb-2 text-right font-medium">{t('shift.methodRefunded')}</th>
                <th className="pb-2 text-right font-medium">{t('shift.methodNet')}</th>
              </tr>
            </thead>
            <tbody>
              {(detail?.byMethod ?? []).map((row) => (
                <tr key={row.method} className="border-t border-line last:border-0">
                  <td className="py-2 text-ink">{methodLabel(t, row.method)}</td>
                  <td className="py-2 text-right tabular-nums text-ink">{row.payments}</td>
                  <td className="py-2 text-right tabular-nums text-ink">
                    {money(row.capturedMinor)}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {row.refundedMinor > 0 ? (
                      <span className="text-warn">{money(row.refundedMinor)}</span>
                    ) : (
                      <span className="text-ink-faint">—</span>
                    )}
                  </td>
                  <td className="py-2 text-right tabular-nums font-medium text-ink">
                    {money(row.netMinor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      <Panel title={t('tab.shifts')}>
        <Table>
          <thead>
            <tr className="text-left text-ink-muted">
              <th className="pb-2 font-medium">{t('shift.openedAt')}</th>
              <th className="pb-2 font-medium">{t('shift.status')}</th>
              <th className="pb-2 text-right font-medium">{t('shift.opening')}</th>
              <th className="pb-2 text-right font-medium">{t('shift.closing')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <SkeletonRows rows={5} columns={6} /> : null}
            {shifts.map((s) => (
              <tr
                key={s.id}
                onClick={() => void fetchShift(s.id).then(setDetail)}
                className="cursor-pointer border-t border-line hover:bg-sunken"
              >
                <td className="py-2 text-ink">{new Date(s.openedAt).toLocaleString()}</td>
                <td className="py-2 text-ink-muted">{t(`shift.status.${s.status}` as PosKey)}</td>
                <td className="py-2 text-right tabular-nums">{money(s.cashOpeningMinor)}</td>
                <td className="py-2 text-right tabular-nums">
                  {s.cashClosingMinor === null ? '—' : money(s.cashClosingMinor)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>
    </div>
  );
}

/** Сетевая привязка роли, управляющей каталогом: сетевые цены и справочник — только ей. */
function networkCatalog(user: CurrentUser): boolean {
  return user.bindings.some(
    (b) => b.siteId === null && (b.role === 'site_manager' || b.role === 'network_admin'),
  );
}

function CatalogTab({
  siteId,
  t,
  network,
  canManage,
}: {
  siteId: string;
  t: PosTranslate;
  network: boolean;
  canManage: boolean;
}) {
  const [entries, setEntries] = useState<readonly CatalogEntry[]>([]);
  // Где действует новая цена. Сетевую видит и меняет только сетевая роль:
  // управляющий объекта меняет цену своего объекта, сервер проверит то же.
  const [scope, setScope] = useState<'site' | 'network'>('site');
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  // История цены раскрывается строкой под услугой, а не панелью снизу экрана:
  // сравнивать «было — стало» удобно там же, где стоит текущая цена.
  const [openHistory, setOpenHistory] = useState<string | null>(null);
  const [history, setHistory] = useState<readonly PriceHistoryRow[] | null>(null);

  const toggleHistory = async (serviceId: string): Promise<void> => {
    if (openHistory === serviceId) {
      setOpenHistory(null);
      return;
    }
    setOpenHistory(serviceId);
    setHistory(null);
    try {
      setHistory(await priceHistory(serviceId, siteId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setHistory([]);
    }
  };

  const load = useCallback(async (): Promise<void> => {
    setBusy(true);
    try {
      setEntries(await fetchCatalog(siteId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const apply = async (serviceId: string): Promise<void> => {
    const draft = drafts[serviceId];
    if (!draft) return;
    setBusy(true);
    setError(undefined);
    try {
      await setTariff({
        serviceId,
        siteId: scope === 'network' && network ? null : siteId,
        priceMinor: Math.round(Number(draft) * 100),
      });
      setDrafts((prev) => ({ ...prev, [serviceId]: '' }));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
    <Panel title={t('tab.catalog')}>
      {canManage ? (
        <div className="mb-3 flex items-end gap-2">
          <Field label={t('catalog.scopePick')}>
            <Select value={scope} onChange={(e) => setScope(e.target.value as 'site' | 'network')}>
              <option value="site">{t('catalog.scopeSiteOnly')}</option>
              {network ? <option value="network">{t('catalog.scopeNetwork')}</option> : null}
            </Select>
          </Field>
        </div>
      ) : null}
      {error ? (
        <div className="mb-3 rounded border border-danger-line bg-danger-bg px-4 py-2 text-sm text-danger">
          {error}
        </div>
      ) : null}
      <Table>
        <thead>
          <tr className="text-left text-ink-muted">
            <th className="pb-2 font-medium">{t('catalog.service')}</th>
            <th className="pb-2 font-medium">{t('catalog.code')}</th>
            <th className="pb-2 text-right font-medium">{t('catalog.price')}</th>
            <th className="pb-2 font-medium">{t('catalog.scope')}</th>
            <th className="pb-2 font-medium">{t('catalog.newPrice')}</th>
            <th className="pb-2 font-medium">{t('catalog.history')}</th>
          </tr>
        </thead>
        <tbody>
          {loading ? <SkeletonRows rows={5} columns={6} /> : null}
          {entries.map((entry) => (
            <tr key={entry.serviceId} className="border-t border-line">
              <td className="py-2 text-ink">{entry.name}</td>
              <td className="py-2 text-ink-muted">{entry.code}</td>
              <td className="py-2 text-right tabular-nums text-ink">
                {entry.price ? money(entry.price.priceMinor) : t('catalog.noPrice')}
              </td>
              <td className="py-2 text-ink-muted">
                {entry.price ? t(`catalog.scope.${entry.price.scope}` as PosKey) : '—'}
              </td>
              <td className="py-2">
                <div className="flex items-center gap-2">
                  <TextInput
                    value={drafts[entry.serviceId] ?? ''}
                    onChange={(e) =>
                      setDrafts((prev) => ({ ...prev, [entry.serviceId]: e.target.value }))
                    }
                    inputMode="numeric"
                    className="w-28"
                  />
                  <button
                    type="button"
                    disabled={busy || !drafts[entry.serviceId]}
                    onClick={() => void apply(entry.serviceId)}
                    className="inline-flex h-7 items-center justify-center rounded-control bg-brand-600 px-2.5 text-xs font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
                  >
                    {t('catalog.apply')}
                  </button>
                </div>
              </td>
              <td className="py-2">
                <button
                  type="button"
                  className="text-xs text-brand-700 hover:underline"
                  onClick={() => void toggleHistory(entry.serviceId)}
                >
                  {openHistory === entry.serviceId ? t('catalog.historyHide') : t('catalog.historyShow')}
                </button>
              </td>
            </tr>
          ))}
          {openHistory ? (
            <tr className="border-t border-line bg-sunken">
              <td colSpan={6} className="px-2 py-3">
                <div className="text-xs font-medium text-ink">{t('catalog.historyTitle')}</div>
                {history === null ? (
                  <p className="mt-1 text-xs text-ink-muted">{t('catalog.historyLoading')}</p>
                ) : history.length === 0 ? (
                  <p className="mt-1 text-xs text-ink-muted">{t('catalog.historyEmpty')}</p>
                ) : (
                  <ul className="mt-2 flex flex-col gap-1">
                    {history.map((row) => (
                      <li key={row.tariffId} className="flex flex-wrap gap-3 text-xs">
                        <span className="tabular-nums font-medium text-ink">
                          {money(row.priceMinor)}
                        </span>
                        <span className="text-ink-muted">
                          {formatMoment(row.validFrom)} →{' '}
                          {row.validTo ? formatMoment(row.validTo) : t('catalog.historyNow')}
                        </span>
                        <span className="text-ink-muted">
                          {t(`catalog.scope.${row.scope}` as PosKey)}
                          {row.sizeClass ? ` · ${row.sizeClass.toUpperCase()}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
      <p className="mt-3 text-xs text-ink-muted">{t('catalog.hint')}</p>
    </Panel>
    <ServicesPanel
      siteId={siteId}
      t={t}
      network={network}
      canManage={canManage}
      onChanged={() => void load()}
    />
    <PackagesPanel siteId={siteId} t={t} network={network} canManage={canManage} />
    </div>
  );
}

/**
 * Материалы. Раньше вкладка показывала «Данных нет» и НОЛЬ интерактивных
 * элементов: завести материал, норму или движение из админки было нельзя, хотя
 * все три эндпоинта существуют. Обещанный ответ на вопрос «почему на этом
 * объекте себестоимость выше» был недостижим.
 */
function MaterialsTab({
  siteId,
  t,
  canManage,
}: {
  siteId: string;
  t: PosTranslate;
  canManage: boolean;
}) {
  const toast = useToast();
  const [materials, setMaterials] = useState<readonly MaterialRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [stock, setStock] = useState<readonly StockRow[]>([]);
  const [services, setServices] = useState<readonly CatalogEntry[]>([]);
  const [busy, setBusy] = useState(false);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [unit, setUnit] = useState<'l' | 'ml' | 'kg' | 'g' | 'pc'>('l');

  const [moveMaterial, setMoveMaterial] = useState('');
  const [moveKind, setMoveKind] = useState<'receipt' | 'writeoff' | 'adjustment'>('receipt');
  const [moveQty, setMoveQty] = useState('');
  const [moveComment, setMoveComment] = useState('');

  const [normService, setNormService] = useState('');
  const [normMaterial, setNormMaterial] = useState('');
  const [normQty, setNormQty] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      const data = await fetchMaterials(siteId);
      setMaterials(data.materials);
      setStock(data.stock);
      setServices(await fetchCatalog(siteId));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [siteId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function guard(run: () => Promise<unknown>, okText: string): Promise<void> {
    setBusy(true);
    try {
      await run();
      await load();
      toast.ok(okText);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Panel title={t('material.stockTitle')}>
        {stock.length === 0 ? (
          <EmptyState
            title={t('common.empty')}
            reason={canManage ? t('material.emptyReason') : undefined}
          />
        ) : (
          <Table>
            <thead>
              <tr className="text-left text-ink-muted">
                <th className="pb-2 font-medium">{t('material.name')}</th>
                <th className="pb-2 font-medium">{t('material.unit')}</th>
                <th className="pb-2 text-right font-medium">{t('material.stock')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <SkeletonRows rows={4} columns={4} /> : null}
              {stock.map((row) => (
                <tr key={row.materialId} className="border-t border-line">
                  <td className="py-2 text-ink">{row.name}</td>
                  <td className="py-2 text-ink-muted">{row.unit}</td>
                  <td
                    className={`py-2 text-right tabular-nums ${row.qty < 0 ? 'text-danger' : 'text-ink'}`}
                  >
                    {row.qty}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <p className="mt-3 text-xs text-ink-muted">{t('material.derivedHint')}</p>
      </Panel>

      {canManage ? (
        <>
          <Panel title={t('material.newTitle')}>
            <div className="grid gap-3 md:grid-cols-4">
              <Field label={t('material.code')} required>
                <TextInput value={code} onChange={(e) => setCode(e.target.value)} />
              </Field>
              <Field label={t('material.name')} required>
                <TextInput value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label={t('material.unit')}>
                <Select value={unit} onChange={(e) => setUnit(e.target.value as typeof unit)}>
                  {(['l', 'ml', 'kg', 'g', 'pc'] as const).map((u) => (
                    <option key={u} value={u}>
                      {t(`material.unit.${u}` as PosKey)}
                    </option>
                  ))}
                </Select>
              </Field>
              <div className="flex items-end">
                <Button
                  tone="primary"
                  disabled={busy || !code.trim() || !name.trim()}
                  onClick={() =>
                    void guard(
                      () => saveMaterial({ code: code.trim(), name: name.trim(), unit }),
                      t('material.saved'),
                    ).then(() => {
                      setCode('');
                      setName('');
                    })
                  }
                >
                  {t('material.save')}
                </Button>
              </div>
            </div>
          </Panel>

          <Panel title={t('material.movementTitle')}>
            <div className="grid gap-3 md:grid-cols-5">
              <Field label={t('material.name')} required>
                <Select value={moveMaterial} onChange={(e) => setMoveMaterial(e.target.value)}>
                  <option value="">—</option>
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('material.movementKind')}>
                <Select
                  value={moveKind}
                  onChange={(e) => setMoveKind(e.target.value as typeof moveKind)}
                >
                  <option value="receipt">{t('material.kind.receipt')}</option>
                  <option value="writeoff">{t('material.kind.writeoff')}</option>
                  <option value="adjustment">{t('material.kind.adjustment')}</option>
                </Select>
              </Field>
              <Field label={t('material.qty')} hint={t('material.qtyHint')} required>
                <TextInput
                  inputMode="decimal"
                  value={moveQty}
                  onChange={(e) => setMoveQty(e.target.value)}
                />
              </Field>
              <Field label={t('common.comment')}>
                <TextInput value={moveComment} onChange={(e) => setMoveComment(e.target.value)} />
              </Field>
              <div className="flex items-end">
                <Button
                  tone="primary"
                  disabled={busy || !moveMaterial || !moveQty.trim()}
                  onClick={() =>
                    void guard(
                      () =>
                        recordMovement({
                          siteId,
                          materialId: moveMaterial,
                          kind: moveKind,
                          qty: Number(moveQty.replace(',', '.')),
                          ...(moveComment.trim() ? { comment: moveComment.trim() } : {}),
                        }),
                      t('material.movementSaved'),
                    ).then(() => {
                      setMoveQty('');
                      setMoveComment('');
                    })
                  }
                >
                  {t('material.movementSave')}
                </Button>
              </div>
            </div>
          </Panel>

          <Panel title={t('material.normTitle')}>
            <p className="mb-3 text-xs text-ink-muted">{t('material.normHint')}</p>
            <div className="grid gap-3 md:grid-cols-4">
              <Field label={t('catalog.service')} required>
                <Select value={normService} onChange={(e) => setNormService(e.target.value)}>
                  <option value="">—</option>
                  {services.map((s) => (
                    <option key={s.serviceId} value={s.serviceId}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('material.name')} required>
                <Select value={normMaterial} onChange={(e) => setNormMaterial(e.target.value)}>
                  <option value="">—</option>
                  {materials.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('material.normQty')} required>
                <TextInput
                  inputMode="decimal"
                  value={normQty}
                  onChange={(e) => setNormQty(e.target.value)}
                />
              </Field>
              <div className="flex items-end">
                <Button
                  tone="primary"
                  disabled={busy || !normService || !normMaterial || !normQty.trim()}
                  onClick={() =>
                    void guard(
                      () =>
                        setNorm({
                          serviceId: normService,
                          materialId: normMaterial,
                          qtyPerUnit: Number(normQty.replace(',', '.')),
                          siteId,
                        }),
                      t('material.normSaved'),
                    ).then(() => setNormQty(''))
                  }
                >
                  {t('material.normSave')}
                </Button>
              </div>
            </div>
          </Panel>
        </>
      ) : null}
    </div>
  );
}

/**
 * Табель. Показывал «Минут: 0» у сотрудников с полусотней заказов каждый:
 * ручного ввода не было, а связать выработку со временем без него нельзя.
 */
function TimesheetTab({
  siteId,
  t,
  canManage,
}: {
  siteId: string;
  t: PosTranslate;
  canManage: boolean;
}) {
  const toast = useToast();
  const [results, setResults] = useState<readonly EmployeeResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [cashiers, setCashiers] = useState<readonly CashierOption[]>([]);
  const [busy, setBusy] = useState(false);

  const [entries, setEntries] = useState<readonly TimesheetEntry[]>([]);
  // Период табеля: по умолчанию — с начала месяца по сегодня.
  const [periodFrom, setPeriodFrom] = useState(() => new Date().toISOString().slice(0, 8) + '01');
  const [periodTo, setPeriodTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [employeeId, setEmployeeId] = useState('');
  const [day, setDay] = useState(() => new Date().toISOString().slice(0, 10));
  const [from, setFrom] = useState('09:00');
  const [to, setTo] = useState('18:00');
  const [comment, setComment] = useState('');

  const range = useCallback(
    () => ({
      from: periodFrom ? new Date(`${periodFrom}T00:00:00`).toISOString() : undefined,
      to: periodTo ? new Date(`${periodTo}T23:59:59`).toISOString() : undefined,
    }),
    [periodFrom, periodTo],
  );

  const load = useCallback(async (): Promise<void> => {
    try {
      const { from: fromIso, to: toIso } = range();
      const sheet = await fetchTimesheet(siteId, fromIso, toIso);
      setResults(sheet.results);
      setEntries(sheet.entries);
      setCashiers(await fetchCashiers());
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [siteId, toast, range]);

  useEffect(() => {
    void load();
  }, [load]);

  const names = new Map(results.map((r) => [r.employeeId, r.displayName]));
  for (const c of cashiers) if (!names.has(c.employeeId)) names.set(c.employeeId, c.displayName);

  const minutes = (() => {
    const start = Date.parse(`${day}T${from}:00`);
    const end = Date.parse(`${day}T${to}:00`);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
    return Math.round((end - start) / 60000);
  })();

  return (
    <div className="space-y-4">
      <Panel title={t('timesheet.period')}>
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('timesheet.from')}>
            <TextInput type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} />
          </Field>
          <Field label={t('timesheet.to')}>
            <TextInput type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} />
          </Field>
          <Button onClick={() => void load()}>{t('timesheet.show')}</Button>
          <Button
            tone="primary"
            onClick={() => {
              const { from: fromIso, to: toIso } = range();
              void downloadTimesheet(siteId, fromIso, toIso).catch((err: unknown) =>
                toast.error(err instanceof ApiError ? err.message : String(err)),
              );
            }}
          >
            {t('timesheet.export')}
          </Button>
        </div>
      </Panel>
      <Panel title={t('timesheet.results')}>
        {loading ? (
          <TableSkeleton rows={4} columns={4} />
        ) : results.length === 0 ? (
          <EmptyState title={t('common.empty')} />
        ) : (
          <Table>
            <thead>
              <tr className="text-left text-ink-muted">
                <th className="pb-2 font-medium">{t('common.employee')}</th>
                <th className="pb-2 text-right font-medium">{t('timesheet.minutes')}</th>
                <th className="pb-2 text-right font-medium">{t('timesheet.shifts')}</th>
                <th className="pb-2 text-right font-medium">{t('timesheet.orders')}</th>
                <th className="pb-2 text-right font-medium">{t('timesheet.revenue')}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((row) => (
                <tr key={row.employeeId} className="border-t border-line">
                  <td className="py-2 text-ink">{row.displayName}</td>
                  <td
                    className={`py-2 text-right tabular-nums ${row.minutes === 0 ? 'text-ink-faint' : ''}`}
                  >
                    {row.minutes}
                  </td>
                  <td className="py-2 text-right tabular-nums">{row.shifts ?? 0}</td>
                  <td className="py-2 text-right tabular-nums">{row.orders}</td>
                  <td className="py-2 text-right tabular-nums">{money(row.revenueMinor)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {results.some((r) => r.minutes === 0 && r.orders > 0) ? (
          <p className="mt-3 text-xs text-warn">{t('timesheet.zeroHint')}</p>
        ) : null}
        <p className="mt-3 text-xs text-ink-muted">{t('timesheet.revenueHint')}</p>
      </Panel>

      <Panel title={t('timesheet.entries')}>
        {entries.length === 0 ? (
          <EmptyState title={t('common.empty')} />
        ) : (
          <Table>
            <thead>
              <tr className="text-left text-ink-muted">
                <th className="pb-2 font-medium">{t('common.employee')}</th>
                <th className="pb-2 font-medium">{t('timesheet.started')}</th>
                <th className="pb-2 font-medium">{t('timesheet.ended')}</th>
                <th className="pb-2 text-right font-medium">{t('timesheet.minutes')}</th>
                <th className="pb-2 font-medium">{t('services.status')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-t border-line">
                  <td className="py-2 text-ink">{names.get(entry.employeeId) ?? entry.employeeId}</td>
                  <td className="py-2 text-ink-muted">{formatMoment(entry.startedAt)}</td>
                  <td className="py-2 text-ink-muted">{entry.endedAt ? formatMoment(entry.endedAt) : '—'}</td>
                  <td className="py-2 text-right tabular-nums">{entry.minutes ?? '—'}</td>
                  <td className="py-2 text-ink-muted">{t(`timesheet.kind.${entry.kind}` as PosKey)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      {canManage ? (
        <Panel title={t('timesheet.addTitle')}>
          <div className="grid gap-3 md:grid-cols-5">
            <Field label={t('common.employee')} required>
              <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
                <option value="">—</option>
                {cashiers.map((c) => (
                  <option key={c.employeeId} value={c.employeeId}>
                    {c.displayName}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('timesheet.day')}>
              <TextInput type="date" value={day} onChange={(e) => setDay(e.target.value)} />
            </Field>
            <Field label={t('timesheet.from')}>
              <TextInput type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field label={t('timesheet.to')}>
              <TextInput type="time" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <div className="flex items-end">
              <Button
                tone="primary"
                disabled={busy || !employeeId || minutes <= 0}
                onClick={() => {
                  setBusy(true);
                  recordTimesheet({
                    employeeId,
                    siteId,
                    startedAt: new Date(`${day}T${from}:00`).toISOString(),
                    endedAt: new Date(`${day}T${to}:00`).toISOString(),
                    ...(comment.trim() ? { comment: comment.trim() } : {}),
                  })
                    .then(() => load())
                    .then(() => toast.ok(t('timesheet.saved', { minutes })))
                    .catch((err: unknown) =>
                      toast.error(err instanceof ApiError ? err.message : String(err)),
                    )
                    .finally(() => setBusy(false));
                }}
              >
                {t('timesheet.save')}
              </Button>
            </div>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <Field label={t('common.comment')}>
              <TextInput value={comment} onChange={(e) => setComment(e.target.value)} />
            </Field>
            <div className="flex items-end text-sm text-ink-muted">
              {minutes > 0 ? t('timesheet.willAdd', { minutes }) : t('timesheet.badRange')}
            </div>
          </div>
        </Panel>
      ) : null}
    </div>
  );
}

/**
 * Кассовые терминалы.
 *
 * Постановка кассы на учёт, отзыв доступа и глубина очереди офлайн-операций
 * существовали в API с момента сдачи модуля, а ставили кассу на учёт curl-ом.
 * Токен показывается ровно один раз — второго способа его узнать нет и не
 * должно быть: в базе лежит только хеш.
 */
function DevicesTab({ siteId, t }: { siteId: string; t: PosTranslate }) {
  const toast = useToast();
  const [sites, setSites] = useState<readonly SiteSummary[]>([]);
  const [rows, setRows] = useState<readonly PosDevice[] | null>(null);
  /*
   * Объект постановки на учёт держится отдельно от объекта оболочки: список
   * терминалов следует за переключателем сверху, а завести кассу можно и для
   * соседнего объекта, не переключаясь туда. По умолчанию — текущий.
   */
  const [target, setTarget] = useState(siteId);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<{ deviceId: string; token: string } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setRows(await fetchPosDevices(siteId));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
      setRows([]);
    }
    // Зависимость от объекта обязательна: без неё переключатель сверху меняет
    // заголовок запроса, но сам запрос не повторяется, и список остаётся от
    // первого открытого объекта.
  }, [siteId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadSites().then(setSites);
  }, []);

  async function enroll(): Promise<void> {
    setBusy(true);
    try {
      const created = await enrollPosDevice({
        siteId: target,
        ...(label.trim() ? { label: label.trim() } : {}),
      });
      setIssued(created);
      setLabel('');
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(deviceId: string): Promise<void> {
    setBusy(true);
    try {
      await revokePosDevice(deviceId);
      toast.ok(t('devices.revoked'));
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Panel title={t('tab.devices')}>
        {rows === null ? (
          <TableSkeleton rows={3} columns={5} />
        ) : rows.length === 0 ? (
          <EmptyState title={t('devices.empty')} reason={t('devices.emptyHint')} />
        ) : (
          <Table>
            <thead>
              <tr className="text-left text-ink-muted">
                <th className="pb-2 font-medium">{t('devices.label')}</th>
                <th className="pb-2 font-medium">{t('devices.version')}</th>
                <th className="pb-2 text-right font-medium">{t('devices.queue')}</th>
                <th className="pb-2 font-medium">{t('devices.seen')}</th>
                <th className="pb-2 font-medium">{t('devices.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.deviceId} className="border-t border-line">
                  <td className="py-2 text-ink">
                    {row.label || row.deviceId}
                    <div className="font-mono text-xs text-ink-muted">{row.deviceId}</div>
                  </td>
                  <td className="py-2 text-ink-muted">{row.appVersion ?? '—'}</td>
                  <td
                    className={`py-2 text-right tabular-nums ${
                      row.queueDepth > 0 ? 'text-warn' : 'text-ink-muted'
                    }`}
                  >
                    {row.queueDepth}
                  </td>
                  <td className="py-2 text-ink-muted">{formatMoment(row.lastSeenAt)}</td>
                  <td className="py-2">
                    {row.status === 'active' ? (
                      <button
                        type="button"
                        disabled={busy}
                        className="text-xs text-danger hover:underline disabled:text-ink-faint"
                        onClick={() => void revoke(row.deviceId)}
                      >
                        {t('devices.revoke')}
                      </button>
                    ) : (
                      <span className="text-xs text-ink-muted">{t('devices.revokedLabel')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      <Panel title={t('devices.add')}>
        <div className="flex flex-wrap items-end gap-2">
          {/*
            Объект показан явно, хотя берётся из переключателя оболочки: касса
            ставится на учёт на конкретный объект, и ошибиться здесь — значит
            выдать токен не тому посту. Раньше объект нигде не назывался, и
            понять, куда уйдёт касса, было нельзя.
          */}
          <Field label={t('devices.site')}>
            <Select value={target} onChange={(e) => setTarget(e.target.value)}>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name} · {site.code}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('devices.label')}>
            <TextInput value={label} onChange={(e) => setLabel(e.target.value)} />
          </Field>
          <Button tone="primary" disabled={busy || !target} onClick={() => void enroll()}>
            {t('devices.enroll')}
          </Button>
        </div>

        {issued ? (
          <div className="mt-3 rounded border border-warn-line bg-warn-bg px-3 py-2 text-sm text-warn">
            <p className="font-medium">{t('devices.tokenOnce')}</p>
            <code className="mt-1 block font-mono text-xs break-all">{issued.token}</code>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

/**
 * Деньги у провайдера, которые ждут человека: оплата подтверждена после
 * того, как заказ закрыли иначе, или на другую сумму, — клиенту надо
 * вернуть деньги; или провайдер так и не назвал итог. Раньше пометка
 * `refundRequired` лежала в базе, и её не видел никто.
 */
function AttentionTab({ siteId, t, canResolve }: { siteId: string; t: PosTranslate; canResolve: boolean }) {
  const toast = useToast();
  const [rows, setRows] = useState<readonly AttentionPayment[] | null>(null);
  const [references, setReferences] = useState<Readonly<Record<string, string>>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      setRows(await fetchAttentionPayments(siteId));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
      setRows([]);
    }
  }, [siteId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function resolve(paymentId: string): Promise<void> {
    const reference = references[paymentId]?.trim();
    if (!reference) return;
    setBusy(true);
    try {
      await resolveAttentionPayment(paymentId, { reference });
      toast.ok(t('attention.resolved'));
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={t('tab.attention')}>
      {rows === null ? (
        <TableSkeleton rows={3} columns={4} />
      ) : rows.length === 0 ? (
        <EmptyState title={t('attention.empty')} reason={t('attention.emptyHint')} />
      ) : (
        <Table>
          <thead>
            <tr className="text-left text-ink-muted">
              <th className="pb-2 font-medium">{t('attention.when')}</th>
              <th className="pb-2 font-medium">{t('attention.what')}</th>
              <th className="pb-2 text-right font-medium">{t('common.amount')}</th>
              <th className="pb-2 font-medium">{t('attention.reference')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.paymentId} className="border-t border-line align-top">
                <td className="py-2 text-ink-muted">{formatMoment(row.createdAt)}</td>
                <td className="py-2 text-ink">
                  {t(`attention.kind.${row.kind}` as PosKey)}
                  {row.reason ? (
                    <div className="text-xs text-ink-muted">{t(`attention.reason.${row.reason}` as PosKey)}</div>
                  ) : null}
                  <div className="font-mono text-xs text-ink-muted">
                    {methodLabel(t, row.method)} · {row.provider ?? '—'} · {row.externalId ?? '—'}
                  </div>
                </td>
                <td className="py-2 text-right tabular-nums">{money(row.amountMinor)}</td>
                <td className="py-2">
                  {canResolve ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <TextInput
                        aria-label={t('attention.reference')}
                        value={references[row.paymentId] ?? ''}
                        onChange={(e) => setReferences({ ...references, [row.paymentId]: e.target.value })}
                      />
                      <Button
                        disabled={busy || !(references[row.paymentId] ?? '').trim()}
                        onClick={() => void resolve(row.paymentId)}
                      >
                        {t('attention.resolve')}
                      </Button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Panel>
  );
}

export default function PosModule({ title, user, siteId }: ModuleViewProps) {
  const t = useMemo(() => makePosT(user.locale), [user.locale]);
  const visible = useMemo(
    () => TABS.filter((entry) => allowsAnywhere(user, ...entry.needs)),
    [user],
  );
  const [tab, setTab] = useTab<TabKey>(
    'pos',
    visible.map((entry) => entry.key),
    visible[0]?.key ?? 'shifts',
  );
  const canManage = allowsAnywhere(user, 'catalog.manage', 'admin.manage');
  const canReconcile = allows(user, 'shift.reconcile');
  const network = networkCatalog(user);

  if (!siteId) {
    return (
      <ModulePage title={title}>
        <EmptyState title={t('common.siteRequired')} reason={t('common.siteRequiredHint')} />
      </ModulePage>
    );
  }

  return (
    <ModulePage
      title={title}
      subtitle={t('module.subtitle')}
      tabs={visible.map((entry) => ({ key: entry.key, label: t(entry.label) }))}
      activeTab={tab}
      onTab={setTab}
    >
      {tab === 'shifts' ? <ShiftsTab siteId={siteId} t={t} canReconcile={canReconcile} /> : null}
      {tab === 'catalog' ? (
        <CatalogTab siteId={siteId} t={t} network={network} canManage={allows(user, 'catalog.manage')} />
      ) : null}
      {tab === 'materials' ? <MaterialsTab siteId={siteId} t={t} canManage={canManage} /> : null}
      {tab === 'timesheet' ? <TimesheetTab siteId={siteId} t={t} canManage={canManage} /> : null}
      {tab === 'devices' ? <DevicesTab siteId={siteId} t={t} /> : null}
      {tab === 'attention' ? (
        <AttentionTab siteId={siteId} t={t} canResolve={allowsAnywhere(user, 'order.refund')} />
      ) : null}
    </ModulePage>
  );
}
